import { z } from 'zod'

import { zSelect } from 'Lib/zod'

import { FORM_MESSAGES_MAP } from 'Constants/formMessages'
import {
  RETURN_LABEL_NAME_MAX_LENGTH,
  RETURN_LABEL_SENDER_NAME_MAX_LENGTH,
} from 'Constants/returnLabel'

import { DeviceIdentifierTypeEnum } from './PublicRequestReturnLabel.types'

const buildDeviceIdentifierSchema = (requireDeviceIdentifier: boolean) =>
  z.object({
    identifierType: z.nativeEnum(DeviceIdentifierTypeEnum, {
      errorMap: () => ({ message: FORM_MESSAGES_MAP.REQUIRED }),
    }),
    value: requireDeviceIdentifier
      ? z.string().trim().min(1, FORM_MESSAGES_MAP.REQUIRED)
      : z.string(),
  })

export const getPublicRequestReturnLabelFormSchema = (
  requireDeviceIdentifier: boolean,
) =>
  z.object({
    city: z.string().trim().min(1, FORM_MESSAGES_MAP.REQUIRED),
    // Becomes sender_address.company on the carrier payload, so DHL's limit
    // applies to it directly.
    companyName: z
      .string()
      .trim()
      .min(1, FORM_MESSAGES_MAP.REQUIRED)
      .max(RETURN_LABEL_NAME_MAX_LENGTH, FORM_MESSAGES_MAP.MAX_LENGTH),
    contactEmail: z.string().trim().email(FORM_MESSAGES_MAP.EMAIL),
    // Uncapped: contactName stays on the request as the requester's
    // notification name and never reaches the carrier, so DHL's limit does not
    // apply to it (SM-2442).
    contactName: z.string().trim().min(1, FORM_MESSAGES_MAP.REQUIRED),
    country: zSelect.refine(arg => !!arg?.value, FORM_MESSAGES_MAP.REQUIRED),
    devices: z.array(buildDeviceIdentifierSchema(requireDeviceIdentifier)),
    senderEmail: z.string().trim().email(FORM_MESSAGES_MAP.EMAIL).or(z.literal('')).optional(),
    // Becomes sender_address.name on the carrier payload - the name DHL prints
    // on the label. Capped at its own, looser limit than the company name.
    senderName: z
      .string()
      .trim()
      .min(1, FORM_MESSAGES_MAP.REQUIRED)
      .max(RETURN_LABEL_SENDER_NAME_MAX_LENGTH, FORM_MESSAGES_MAP.MAX_LENGTH),
    senderPhone: z.string().trim().optional(),
    street: z.string().trim().min(1, FORM_MESSAGES_MAP.REQUIRED),
    streetNumber: z.string().trim().min(1, FORM_MESSAGES_MAP.REQUIRED),
    zipcode: z.string().trim().min(1, FORM_MESSAGES_MAP.REQUIRED),
  })

const submitReturnLabelResponseRawSchema = z
  .object({
    has_dangerous_goods: z.boolean(),
    return_label_request_id: z.number(),
    show_customer_support_card: z.boolean(),
    show_label_coming_soon: z.boolean(),
    status: z.string(),
    tracking_reference: z.string().trim(),
  })
  // A tracking reference only exists when a label is actually generated. When
  // show_customer_support_card or show_label_coming_soon is true the request is
  // routed elsewhere and no label is created, so the BE returns an empty
  // tracking_reference. Require it non-empty only on the success path, where the
  // success view relies on it.
  .refine(
    response =>
      response.show_customer_support_card ||
      response.show_label_coming_soon ||
      response.tracking_reference.length > 0,
    { path: ['tracking_reference'] },
  )

export const submitReturnLabelResponseSchema =
  submitReturnLabelResponseRawSchema.transform(
    ({
      has_dangerous_goods,
      return_label_request_id,
      show_customer_support_card,
      show_label_coming_soon,
      tracking_reference,
    }) => ({
      hasDangerousGoods: has_dangerous_goods,
      requestId: return_label_request_id,
      showCustomerSupportCard: show_customer_support_card,
      showLabelComingSoon: show_label_coming_soon,
      trackingReference: tracking_reference,
    }),
  )

export type SubmitReturnLabelResponse = z.infer<
  typeof submitReturnLabelResponseSchema
>

export const checkReturnLabelTokenResponseSchema = z.object({
  org_country: z.string().trim().nullish(),
  prefilled: z.boolean(),
  regenerated: z.boolean(),
  token: z.string().trim().min(1),
})

export type CheckReturnLabelTokenResponse = z.infer<
  typeof checkReturnLabelTokenResponseSchema
>
